import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, X, SendHorizontal, Loader2 } from 'lucide-react';
import { useEstimator } from '../../context/EstimatorContext';
import { track } from '../../lib/site';
import { useChatStream } from '../../hooks/useChatStream';

const SUGGESTIONS = [
  'What is EPS construction?',
  'Is it good for a farmhouse?',
  'How fast can you build?',
  'Can I use normal finishes?',
];

const ChatMessage = ({ message }) => (
  <div className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
    <div
      className={
        message.role === 'user'
          ? 'bg-[#155E6D] text-white rounded-xl rounded-tr-sm px-4 py-2.5 text-sm leading-relaxed max-w-[85%]'
          : 'bg-muted rounded-xl rounded-tl-sm px-4 py-2.5 text-sm leading-relaxed max-w-[85%] whitespace-pre-wrap'
      }
    >
      {message.content || (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…
        </span>
      )}
    </div>
  </div>
);

const ChatWelcome = ({ onSuggestion }) => (
  <div>
    <div className="bg-muted rounded-xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed max-w-[85%]">
      Hello — I'm the MELOPA INFRA assistant. Ask me anything about EPS wall &amp; roof construction, or tell me
      about the project you're planning.
    </div>
    <div className="mt-4 flex flex-wrap gap-2">
      {SUGGESTIONS.map((s, idx) => (
        <button
          key={s}
          type="button"
          data-testid={`chat-suggestion-${idx}`}
          onClick={() => onSuggestion(s)}
          className="text-xs min-h-[44px] px-4 py-2 rounded-full border border-border hover:bg-muted transition-[background-color] text-foreground/80"
        >
          {s}
        </button>
      ))}
    </div>
  </div>
);

export const ChatWidget = () => {
  const { openEstimator, isOpen: estimatorOpen } = useEstimator();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const { messages, streaming, sendMessage } = useChatStream();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    setInput('');
    track('chat_message_sent', {});
    sendMessage(message);
  };

  if (estimatorOpen) return null;

  return (
    <>
      {/* Launcher: icon-only on mobile (avoids covering content above the
          sticky CTA), labeled pill on larger screens */}
      {!open && (
        <button
          type="button"
          aria-label="Ask MELOPA AI about EPS construction"
          data-testid="chat-widget-launcher"
          onClick={() => {
            setOpen(true);
            track('chat_opened', {});
          }}
          className="fixed z-40 right-4 bottom-24 md:right-6 md:bottom-[5.5rem] h-12 w-12 md:w-auto md:pl-4 md:pr-5 rounded-full bg-[#14181B] text-white shadow-[0_14px_40px_rgba(20,24,27,0.30)] flex items-center justify-center md:justify-start gap-2 hover:bg-[#22282d] transition-[background-color]"
        >
          <Sparkles className="w-5 h-5 md:w-4 md:h-4 text-[#E2A26B]" />
          <span className="hidden md:inline text-sm font-medium">Ask MELOPA AI</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed z-50 inset-x-2 bottom-20 md:inset-x-auto md:right-6 md:bottom-6 md:w-[390px] bg-background border border-border rounded-2xl shadow-[0_30px_90px_rgba(20,24,27,0.25)] flex flex-col overflow-hidden h-[68vh] md:h-[560px]"
          role="dialog"
          aria-label="MELOPA AI assistant"
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <div className="bg-[#14181B] text-white px-5 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#E2A26B]" />
              </span>
              <div>
                <p className="font-display font-semibold text-sm tracking-wide">MELOPA AI</p>
                <p className="text-[11px] text-white/55">EPS construction assistant</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close chat"
              data-testid="chat-widget-close"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/10 transition-[background-color]"
            >
              <X className="w-4.5 h-4.5" size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-widget-messages">
            {messages.length === 0 && <ChatWelcome onSuggestion={send} />}
            {messages.map((m) => (
              <ChatMessage key={m.id} message={m} />
            ))}
          </div>

          {/* Estimator nudge */}
          <div className="px-4 pb-2 shrink-0">
            <button
              type="button"
              data-testid="chat-widget-estimate-button"
              onClick={() => {
                setOpen(false);
                track('hero_estimate_click', { placement: 'chat_widget' });
                openEstimator();
              }}
              className="w-full h-10 rounded-lg bg-[#155E6D]/[0.07] border border-[#155E6D]/25 text-[#155E6D] text-xs font-medium tracking-wide hover:bg-[#155E6D]/[0.12] transition-[background-color]"
            >
              GET PROJECT ESTIMATE →
            </button>
          </div>

          {/* Input */}
          <form
            className="border-t border-border p-3 flex gap-2 shrink-0"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about EPS construction…"
              maxLength={1000}
              data-testid="chat-widget-input"
              className="flex-1 h-11 px-4 rounded-lg border border-border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <button
              type="submit"
              aria-label="Send message"
              data-testid="chat-widget-send"
              disabled={streaming || !input.trim()}
              className="h-11 w-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 hover:bg-primary/90 transition-[background-color]"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizontal className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};
